/**
 * SizilPro — Setup Step Primitives
 * Shared form elements for the dark-theme wizard UI.
 */

import {
  forwardRef,
  type ReactNode,
  type InputHTMLAttributes,
  type TextareaHTMLAttributes,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

// ─── Field group ──────────────────────────────────────────────
interface FieldGroupProps {
  children: ReactNode;
  label?: string;
  description?: string;
}

export function SetupFieldGroup({
  children,
  label,
  description,
}: FieldGroupProps) {
  return (
    <div className="space-y-1.5">
      {label && (
        <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.15em] text-white/40">
          {label}
        </p>
      )}
      {description && <p className="mb-3 text-xs text-white/40">{description}</p>}
      {children}
    </div>
  );
}

// ─── Label ────────────────────────────────────────────────────
interface LabelProps {
  children: ReactNode;
  htmlFor?: string;
  required?: boolean;
}

export function SetupLabel({ children, htmlFor, required }: LabelProps) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-sm font-medium text-white/70"
    >
      {children}
      {required && <span className="ml-0.5 text-red-400">*</span>}
    </label>
  );
}

// ─── Input ────────────────────────────────────────────────────
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
}

export const SetupInput = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error, ...props }, ref) => (
    <div>
      <input
        ref={ref}
        className={cn(
          "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5",
          "text-sm text-white placeholder:text-white/25",
          "appearance-none transition-colors",
          "focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30",
          error && "border-red-500/50",
          className
        )}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  )
);
SetupInput.displayName = "SetupInput";

// ─── Textarea ─────────────────────────────────────────────────
interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
}

export const SetupTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, ...props }, ref) => (
    <div>
      <textarea
        ref={ref}
        className={cn(
          "w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-2.5",
          "text-sm text-white placeholder:text-white/25",
          "transition-colors",
          "focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30",
          error && "border-red-500/50",
          className
        )}
        {...props}
      />
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  )
);
SetupTextarea.displayName = "SetupTextarea";

// ─── Select ───────────────────────────────────────────────────
interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  children: ReactNode;
}

export const SetupSelect = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, error, children, ...props }, ref) => (
    <div>
      <select
        ref={ref}
        className={cn(
          "w-full appearance-none rounded-lg border border-white/10 bg-[#1a1a1a] px-4 py-2.5",
          "text-sm text-white",
          "transition-colors",
          "focus:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30",
          error && "border-red-500/50",
          className
        )}
        style={{ backgroundColor: "#1a1a1a", color: "white" }}
        {...props}
      >
        {children}
      </select>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  )
);
SetupSelect.displayName = "SetupSelect";

// ─── Actions row ─────────────────────────────────────────────
export function SetupActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center justify-end border-t border-white/10 pt-4">
      {children}
    </div>
  );
}

// ─── Section divider ─────────────────────────────────────────
export function SetupDivider({ label }: { label?: string }) {
  if (!label) return <hr className="border-white/10" />;

  return (
    <div className="flex items-center gap-3">
      <hr className="flex-1 border-white/10" />
      <span className="text-[11px] uppercase tracking-[0.15em] text-white/30">
        {label}
      </span>
      <hr className="flex-1 border-white/10" />
    </div>
  );
}